import { SortParam } from 'src/lib/services/api-query-params/api-query-params.types';
import { useApiQueryParams } from 'src/lib/services/api-query-params/use-api-query-params';
import { useTranslation } from 'react-i18next';
import { OptionObject } from 'src/components/ReactSelectInput';
import { useProfile } from 'src/models';
import { roles } from 'src/api/services/RoleClient';

type SortFormat = 'date' | 'number' | 'text' | 'boolean';

type SortItem = {
  label: string;
  keywords?: string[];
  isDate?: boolean;
  format?: SortFormat;
  sort: SortParam;
};

const defaultSortOrderLabels = {
  text: {
    asc: 'A to Z',
    desc: 'Z to A',
  },
  number: {
    asc: 'Lowest to highest',
    desc: 'Highest to lowest',
  },
  date: {
    asc: 'Oldest to newest',
    desc: 'Newest to oldest',
  },
  boolean: {
    asc: 'Not checked',
    desc: 'Checked',
  },
};

type GetSortOrderOptionsFn = (format?: SortFormat) => OptionObject[];

// Save table UI state (sorts/filters) to localStorage using namespaced keys
// Backward compatible: if third arg omitted, defaults to 'sorts'.
const saveToLocalStorage = (
  tableName: string,
  data?: unknown[],
  key: 'sorts' | 'filters' = 'sorts',
) => {
  try {
    const storageKey = `table:${tableName}:${key}`;
    if (Array.isArray(data) && data.length > 0) {
      window.localStorage.setItem(storageKey, JSON.stringify(data));
    } else {
      window.localStorage.removeItem(storageKey);
    }
  } catch (_e) {
    // Ignore storage errors (SSR/private mode)
  }
};

// Remove table UI state (sorts/filters) from localStorage
// Backward compatible: if second arg omitted, defaults to 'sorts'.
const removeFromLocalStorage = (tableName: string, key: 'sorts' | 'filters' = 'sorts') => {
  try {
    const storageKey = `table:${tableName}:${key}`;
    window.localStorage.removeItem(storageKey);
  } catch (_e) {
    // Ignore storage errors (SSR/private mode)
  }
};

const useSorts = () => {
  const { t } = useTranslation();
  const { filters, setFilters } = useApiQueryParams();
  // TODO: Not liking this solution that use-sorts has to have useProfile to sort out QA check, rethink this area
  const { type: authRole } = useProfile({ enabled: false });

  const addSort = (value: SortParam) => {
    setFilters((prev) => ({ ...prev, sorts: [...(prev.sorts ?? []), value] }));
  };

  const updateSort = (id: string, value: SortParam) => {
    setFilters((prev) => {
      const next = prev.sorts;

      if (!next) {
        return prev;
      }

      const index = next.findIndex((s) => s.id === id);

      if (index > -1) {
        next.splice(index, 1, value);
      }

      return {
        ...prev,
        sorts: next,
      };
    });
  };

  const removeSort = (sort: SortParam) => {
    setFilters((prev) => {
      const sorts = prev.sorts?.filter((s) => s.id !== sort.id);

      return {
        ...prev,
        sorts: sorts?.length ? sorts : undefined,
      };
    });
  };

  const clearSorts = () => {
    setFilters({ sorts: undefined });
  };

  const getAvailableSorts = (sortList: SortItem[]) =>
    sortList.filter(
      (sort) =>
        !filters.sorts?.some((s) => s.id === sort.sort.id) &&
        !(
          ![roles.admin, roles.teamLead].includes(authRole as any) && sort.sort.id === 'qa_checked'
        ),
    );

  const getSortOrderOptions: GetSortOrderOptionsFn = (format = 'text') => [
    {
      label: t(`common:sort.${format}.asc`, defaultSortOrderLabels[format].asc),
      value: false,
    },
    {
      label: t(`common:sort.${format}.asc`, defaultSortOrderLabels[format].desc),
      value: true,
    },
  ];

  return {
    sorts: filters.sorts,
    addSort,
    updateSort,
    removeSort,
    clearSorts,
    getAvailableSorts,
    getSortOrderOptions,
  };
};

export { useSorts, saveToLocalStorage, removeFromLocalStorage, type SortItem, type GetSortOrderOptionsFn };
